/**
 * SoftphoneContext (Phase 4 / Chunk 2)
 *
 * Browser softphone state for the CRM module — outbound click-to-call via the
 * Twilio Voice JS SDK. Scoped to CRM pages only (owner decision): the provider
 * wraps the CRM routes, LAZY-loads the SDK (so it never weighs on the platform
 * bundle for non-CRM users), and mints a short-lived AccessToken from
 * POST /api/crm/voice/token, refreshing on `tokenWillExpire`. Twilio carries all
 * media; we never proxy audio.
 *
 * OUTBOUND ONLY this chunk: the Device is initialized for device.connect() but
 * NOT registered, so it cannot receive inbound calls yet (inbound routing +
 * presence + the incoming-call UI land in Chunk 3). If voice env isn't set (or
 * the user isn't entitled) the token endpoint returns 503/403 and we surface
 * `unavailable` — the Call button disables and the rest of the CRM is unaffected.
 *
 * TEST-SAFETY: the number actually dialed is resolved SERVER-SIDE. In test mode
 * every call is redirected to CRM_VOICE_OVERRIDE_TO (your cell) and the real
 * intended number is recorded in the call's communications.metadata — the browser
 * only passes the intended contact number + id.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode, MutableRefObject } from 'react';
import type { Device as TDevice, Call as TCall } from '@twilio/voice-sdk';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { getPref, setPref } from '../lib';

export type CallStatus = 'idle' | 'connecting' | 'ringing' | 'open';

/** One entry in the mic/speaker pickers. `deviceId` is never '' here — the UI
 * adds its own synthetic "System default" option for that. */
export interface AudioDeviceOption { deviceId: string; label: string; }

/** True when this browser can route call audio to a chosen output device
 * (Chrome/Edge). Safari and Firefox can't — the speaker select hides there. */
export const outputSelectionSupported =
  typeof HTMLAudioElement !== 'undefined' && 'setSinkId' in HTMLAudioElement.prototype;

/** Snapshot the browser's audio devices for the pickers. The 'default' /
 * 'communications' pseudo-devices are dropped from the lists (they duplicate a
 * physical device; the UI's "System default" option covers that choice) but the
 * 'default' entry's LABEL is kept — it names the physical device the default
 * resolves to, which is exactly the invisible routing the picker exists to
 * expose (a call quietly riding the laptop mic while a headset sits unused).
 * Devices get generic names when the site has never held mic permission
 * (labels are blank until then — see refreshAudioDevices' probe). */
async function enumerateAudioOptions(): Promise<{
  inputs: AudioDeviceOption[]; outputs: AudioDeviceOption[];
  defaultInputLabel: string; defaultOutputLabel: string; hasLabels: boolean;
} | null> {
  if (!navigator.mediaDevices?.enumerateDevices) return null;
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    let hasLabels = false;
    let defaultInputLabel = '';
    let defaultOutputLabel = '';
    const stripDefault = (label: string) => label.replace(/^(Default|Communications)\s*-\s*/i, '');
    const pick = (kind: MediaDeviceKind, generic: string): AudioDeviceOption[] => {
      const out: AudioDeviceOption[] = [];
      for (const d of all) {
        if (d.kind !== kind) continue;
        if (d.deviceId === 'default' && d.label) {
          if (kind === 'audioinput') defaultInputLabel = stripDefault(d.label);
          else defaultOutputLabel = stripDefault(d.label);
        }
        if (!d.deviceId || d.deviceId === 'default' || d.deviceId === 'communications') continue;
        if (d.label) hasLabels = true;
        out.push({ deviceId: d.deviceId, label: d.label || `${generic} ${out.length + 1}` });
      }
      return out;
    };
    return {
      inputs: pick('audioinput', 'Microphone'),
      outputs: pick('audiooutput', 'Speaker'),
      defaultInputLabel, defaultOutputLabel, hasLabels,
    };
  } catch {
    return null;
  }
}

/** Route call + ringtone audio to the saved speaker ('' = system default).
 * Twilio validates ids against its own device map, so a stale or unplugged id
 * rejects — fall back to the default sink and stay quiet: an audio device
 * preference must never break calling. */
function applySpeakerPref(device: TDevice | null, outputId: string): void {
  const audio: any = (device as any)?.audio;
  if (!audio?.isOutputSelectionSupported) return;
  const route = (coll: any, id: string, fallback: string | null) => {
    try {
      coll?.set(id)?.catch?.(() => {
        if (fallback) { try { coll?.set(fallback)?.catch?.(() => {}); } catch { /* noop */ } }
      });
    } catch { /* noop */ }
  };
  route(audio.speakerDevices, outputId || 'default', outputId ? 'default' : null);
  route(audio.ringtoneDevices, outputId || 'default', outputId ? 'default' : null);
}

/** Point the SDK at the saved mic right before a call leg starts ('' = leave the
 * browser default in charge). Applied at call time rather than at Device init so
 * the tab's mic indicator isn't lit all day (setInputDevice opens the mic and
 * keeps it open); endLocal releases it again after each call. Never rejects —
 * an unplugged saved device silently falls back to the default mic. */
async function applyInputPref(device: TDevice | null, inputId: string): Promise<void> {
  if (!inputId) return;
  const audio: any = (device as any)?.audio;
  if (!audio?.setInputDevice) return;
  try { await audio.setInputDevice(inputId); } catch { /* default mic */ }
}

/**
 * Map a getUserMedia failure to actionable copy. Phones are where this bites:
 * iOS home-screen apps re-ask for the mic every launch, and a reflexive "Don't
 * Allow" leaves every call failing with a silent generic error until the user
 * finds the browser permission setting.
 */
function micErrorMessage(e: unknown): string {
  const name = (e as { name?: string } | null)?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return 'Microphone access is blocked. Allow the microphone for this site and try again (iPhone: aA menu → Website Settings, or Settings → Safari → Microphone; home-screen app: delete and re-allow on next call).';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No microphone was found on this device.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'The microphone is in use by another app. Close it and try again.';
  }
  return 'Could not access the microphone — check the site permission and try again.';
}

export interface ActiveContact {
  contactId?: string;
  to: string;
  name?: string;
}

/** A ringing inbound call. `name`/`contactId` are filled in once the caller's
 * number is matched to a known lead (the popup falls back to the number). The
 * owner / last-contact fields resolve alongside the name from the same lookup so
 * the popup can show who the lead is assigned to and who last reached out. */
export interface IncomingCall {
  from: string | null;
  name?: string | null;
  contactId?: string | null;
  ownerUserId?: string | null;
  ownerName?: string | null;
  lastContactedByName?: string | null;
  lastContactedAt?: string | null;
}

/** The just-ended CONNECTED outbound call (Phase G rating prompt). */
export interface LastCall {
  contactId: string;
  name?: string;
  /** The browser leg IS the parent call — equals the communications row's external_id. */
  callSid: string | null;
  durationMs: number;
}

/** A call parked into a conference, awaiting pickup by any agent (Phase 4b). */
export interface ParkedCall {
  confName: string;
  customerLegSid: string;
  contactId: string | null;
  name: string | null;
  from: string | null;
  parkedBy: string | null;
  parkedAt: string | null;
}

/** While the agent's leg is being handed off, its local end must NOT prompt a rating. */
export type Transition = 'parking' | 'transferring' | null;

interface SoftphoneValue {
  ready: boolean;            // Device initialized (token minted)
  unavailable: boolean;      // voice not configured / not entitled (503/403)
  callStatus: CallStatus;
  inCall: boolean;           // a call is connecting / ringing / open
  muted: boolean;
  /** Media dropped mid-call and the SDK is re-establishing it (mobile network
   *  blips / wifi→cellular handoffs). The call bar shows "Reconnecting…". */
  reconnecting: boolean;
  error: string | null;
  active: ActiveContact | null;
  // a ringing inbound call awaiting answer; name/contactId resolved best-effort
  // when the caller's number matches a known lead.
  incoming: IncomingCall | null;
  // a SECOND inbound call that arrived while already on a call (call-waiting).
  // Surfaced instead of being silently rejected so a callback can't go unseen;
  // the agent can answer it (swapping off the current call) or decline just it.
  waiting: IncomingCall | null;
  startedAt: number | null;  // ms epoch when the call connected (for the timer)
  lastCall: LastCall | null; // just-ended connected outbound call (rating prompt)
  transition: Transition;    // non-null while parking / transferring the live call
  parkedCalls: ParkedCall[]; // calls parked by anyone on the team, awaiting pickup
  startCall: (args: ActiveContact) => Promise<void>;
  hangup: () => void;
  toggleMute: () => void;
  /** Send DTMF tones on the live call (dial-pad keypresses during a call). */
  sendDigits: (digits: string) => void;
  acceptIncoming: () => void;
  rejectIncoming: () => void;
  acceptWaiting: () => void;   // answer the call-waiting call, hanging up the current one
  declineWaiting: () => void;  // decline just the call-waiting call; stay on the current
  park: () => Promise<void>;
  transferBlind: (targetUserId: string) => Promise<void>;
  pickUp: (confName: string, customerLegSid: string) => Promise<void>;
  clearError: () => void;
  clearLastCall: () => void;
  // ---- Audio device selection (mic/speaker picker) ----
  inputDevices: AudioDeviceOption[];
  outputDevices: AudioDeviceOption[];
  inputDeviceId: string;           // '' = system default
  outputDeviceId: string;          // '' = system default
  /** Which physical device "System default" currently resolves to ('' when
   * unknown, e.g. labels not yet unlocked). Shown in the picker so a call
   * silently riding the wrong mic is visible at a glance. */
  defaultInputLabel: string;
  defaultOutputLabel: string;
  outputSelectionSupported: boolean;
  setAudioInputDevice: (deviceId: string) => void;
  setAudioOutputDevice: (deviceId: string) => void;
  refreshAudioDevices: (opts?: { probeForLabels?: boolean }) => Promise<void>;
}

const SoftphoneContext = createContext<SoftphoneValue | null>(null);

export function useSoftphone(): SoftphoneValue {
  const ctx = useContext(SoftphoneContext);
  if (!ctx) throw new Error('useSoftphone must be used within SoftphoneProvider');
  return ctx;
}

export function SoftphoneProvider({ children }: { children: ReactNode }) {
  // Depend on the STABLE user id, NOT the `session` object. The session
  // reference changes on every Supabase token refresh / auth event (and on tab
  // focus); if the init effect below re-ran mid-call, its cleanup would
  // disconnect the LIVE call and destroy the Device — which is exactly what was
  // dropping calls after ~90s. The Twilio access token is refreshed
  // independently via the Device's `tokenWillExpire` event, so the Device never
  // needs re-init on a Supabase refresh.
  const { user } = useAuth();
  const userId = user?.id || null;
  const deviceRef = useRef<TDevice | null>(null);
  const callRef = useRef<TCall | null>(null);
  const incomingRef = useRef<TCall | null>(null);
  // A second inbound Call held while one is already live (call-waiting).
  const waitingRef = useRef<TCall | null>(null);
  // Always points at the latest endLocal so the userId-keyed init effect (which is
  // deliberately NOT re-keyed on callback identity — see the ~90s call-drop note
  // below) can clean up a stale/zombie call ref from inside the 'incoming' handler.
  const endLocalRef = useRef<() => void>(() => {});
  // endLocal is useCallback([], …) and cannot read `active`/`startedAt` state
  // (stale closure) — these refs mirror them so the hang-up handler can stamp
  // lastCall for the Phase G rating prompt.
  const activeRef = useRef<ActiveContact | null>(null);
  const startedAtRef = useRef<number | null>(null);
  // Direction of the live call (set on startCall / acceptIncoming / pickUp) — the
  // server needs it to resolve the CUSTOMER leg for park/transfer.
  const directionRef = useRef<'inbound' | 'outbound' | null>(null);
  // Non-null while the live agent leg is being handed off; endLocal reads it to
  // skip the rating stamp (the call lives on elsewhere, not ended by the agent).
  const transitionRef = useRef<Transition>(null);
  // The live-call payload reported to the server's voice presence (or null when
  // idle). Held in a ref so the periodic heartbeat — which lives inside the
  // userId-keyed init effect and must NOT re-key mid-call — can read the
  // current call state without a fresh closure. The Calls dashboard surfaces it.
  const callPresenceRef = useRef<{ on_call: true; contact_id: string | null; direction: 'outbound' | 'inbound'; started_at: string } | null>(null);

  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [muted, setMuted] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<ActiveContact | null>(null);
  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const [waiting, setWaiting] = useState<IncomingCall | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [lastCall, setLastCall] = useState<LastCall | null>(null);
  const [transition, setTransition] = useState<Transition>(null);
  const [parkedCalls, setParkedCalls] = useState<ParkedCall[]>([]);

  // Mic/speaker preference ('' = system default), persisted per machine — audio
  // hardware is a property of the desk, not the account. Refs mirror the state
  // so call-time application (startCall / accept / pickUp, and the init effect)
  // never needs a fresh closure — re-keying the init effect is the ~90s
  // call-drop bug, and these must not tempt anyone into adding deps to it.
  const [inputDevices, setInputDevices] = useState<AudioDeviceOption[]>([]);
  const [outputDevices, setOutputDevices] = useState<AudioDeviceOption[]>([]);
  const [defaultInputLabel, setDefaultInputLabel] = useState('');
  const [defaultOutputLabel, setDefaultOutputLabel] = useState('');
  const [inputDeviceId, setInputDeviceIdState] = useState<string>(() => getPref('phone.micDeviceId', ''));
  const [outputDeviceId, setOutputDeviceIdState] = useState<string>(() => getPref('phone.speakerDeviceId', ''));
  const inputDeviceIdRef = useRef(inputDeviceId);
  const outputDeviceIdRef = useRef(outputDeviceId);

  /** Re-read the device lists for the pickers. `probeForLabels` (used by the
   * pickers, which only ever mount from a click) briefly opens-and-releases the
   * mic ONCE on machines where the site has never held permission, so real
   * device names show instead of "Microphone 1". */
  const refreshAudioDevices = useCallback(async (opts?: { probeForLabels?: boolean }) => {
    let snap = await enumerateAudioOptions();
    if (opts?.probeForLabels && snap && !snap.hasLabels && navigator.mediaDevices?.getUserMedia) {
      try {
        const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
        probe.getTracks().forEach((t) => t.stop());
        snap = await enumerateAudioOptions();
      } catch { /* denied — generic names still render */ }
    }
    if (snap) {
      setInputDevices(snap.inputs);
      setOutputDevices(snap.outputs);
      setDefaultInputLabel(snap.defaultInputLabel);
      setDefaultOutputLabel(snap.defaultOutputLabel);
    }
  }, []);

  // Keep the picker lists fresh as hardware comes and goes (headset docked,
  // dongle unplugged), and re-route output in case the preferred speaker came
  // back. Its own effect, never folded into the userId-keyed init effect.
  useEffect(() => {
    refreshAudioDevices();
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) return;
    const onChange = () => {
      refreshAudioDevices();
      applySpeakerPref(deviceRef.current, outputDeviceIdRef.current);
    };
    md.addEventListener('devicechange', onChange);
    return () => md.removeEventListener('devicechange', onChange);
  }, [refreshAudioDevices]);

  /** Choose the mic ('' = system default). Live calls hot-swap immediately;
   * otherwise it applies when the next call starts (every call path runs
   * applyInputPref first). */
  const setAudioInputDevice = useCallback((id: string) => {
    inputDeviceIdRef.current = id;
    setInputDeviceIdState(id);
    setPref('phone.micDeviceId', id);
    const audio: any = (deviceRef.current as any)?.audio;
    if (callRef.current && audio?.setInputDevice) {
      // '' targets the browser's 'default' pseudo-device (always present in
      // Chrome); if the id is unknown the SDK rejects and the call simply
      // stays on the mic it already has.
      try { audio.setInputDevice(id || 'default')?.catch?.(() => {}); } catch { /* noop */ }
    }
  }, []);

  /** Choose the speaker ('' = system default). Applies immediately — ringtone
   * and call audio both re-route, live call included. */
  const setAudioOutputDevice = useCallback((id: string) => {
    outputDeviceIdRef.current = id;
    setOutputDeviceIdState(id);
    setPref('phone.speakerDeviceId', id);
    applySpeakerPref(deviceRef.current, id);
  }, []);

  // Initialize the Device once we have an authenticated user. Lazy-load the SDK
  // on demand. Re-runs only on real login/logout/user-switch — never on a token
  // refresh — so an active call is never torn down by an auth event.
  useEffect(() => {
    if (!userId) { setReady(false); setUnavailable(false); return; }
    let cancelled = false;
    let device: TDevice | null = null;
    let presenceTimer: ReturnType<typeof setInterval> | null = null;
    let visibilityHandler: (() => void) | null = null;

    (async () => {
      let token: string | undefined;
      try {
        const res = await api.post('/api/crm/voice/token');
        token = res.data?.token;
        if (!token) throw new Error('no token');
      } catch {
        // 503 not configured / 403 not entitled → quietly unavailable.
        if (!cancelled) { setUnavailable(true); setReady(false); }
        return;
      }
      try {
        const { Device, Call } = await import('@twilio/voice-sdk');
        if (cancelled) return;
        device = new Device(token, {
          // Opus first: forward error correction + adaptive bitrate keep audio
          // usable on lossy cellular links; the SDK's default prefers PCMU
          // (G.711), which degrades badly on packet loss. PCMU stays as the
          // negotiation fallback.
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
          // Ring a second inbound call while one is live. Without this the SDK
          // silently rejects it and the 'incoming' handler's call-waiting path
          // below can never fire.
          allowIncomingWhileBusy: true,
          // Mid-call signaling reconnection (opt-in, off by default): a network
          // blip or a wifi→cellular handoff gets a 30s window to re-attach
          // before Twilio terminates the call, instead of dropping instantly.
          maxCallSignalingTimeoutMs: 30_000,
          // Refresh the access token 60s before expiry (default 10s). Phone
          // browsers throttle background timers hard — a 10s window is easy to
          // miss entirely, leaving the Device unregistered with a dead token.
          tokenRefreshMs: 60_000,
        });
        // Mint + apply a fresh token. Shared by tokenWillExpire, token-related
        // device errors, and the return-to-foreground recovery below. Single-
        // flighted so overlapping triggers (visibility + error) don't stack.
        let refreshingToken = false;
        const refreshToken = async () => {
          if (refreshingToken) return;
          refreshingToken = true;
          try {
            const r = await api.post('/api/crm/voice/token');
            if (r.data?.token) device?.updateToken(r.data.token);
          } catch { /* a later refresh / re-init recovers */ } finally {
            refreshingToken = false;
          }
        };
        device.on('tokenWillExpire', refreshToken);
        device.on('error', (e: any) => {
          console.error('[softphone] device error:', e?.message || e);
          // 20101 invalid / 20104 expired access token — mint a fresh one and
          // re-register so the softphone recovers without a page reload (a
          // phone that slept past the 1h TTL lands here on wake).
          const code = e?.code || e?.twilioError?.code;
          if (code === 20101 || code === 20104) {
            refreshToken().then(() => {
              try {
                if (device && device.state !== Device.State.Registered) device.register().catch(() => {});
              } catch { /* register stays best-effort */ }
            });
          }
        });
        // Incoming calls. If a call is genuinely in progress, surface this one as a
        // CALL-WAITING call (answer/decline) instead of silently rejecting it —
        // silent rejection is exactly what let a lead's callback go unseen while the
        // recruiter was wrapping up the call she had just placed. A stale, already-
        // closed call ref (a missed 'disconnect' event) counts as no active call:
        // clean it up and ring this one normally.
        device.on('incoming', (call: TCall) => {
          const cur = callRef.current;
          let curActive = false;
          if (cur) {
            try { curActive = cur.status() !== 'closed'; } catch { curActive = true; }
            if (!curActive) { try { endLocalRef.current(); } catch { /* noop */ } }
          }
          const from = (call.parameters && call.parameters.From) || null;
          // Resolve a known lead's name + assignment / last-contact context for the
          // popup (best-effort; the number stays as the fallback). Only applies while
          // THIS call is still the slotted one. Applied even when the name is null so
          // the owner / last-contact lines can still show (the from/number fallback
          // in the popup is unaffected — `from` is always carried through).
          const resolveName = (ref: MutableRefObject<TCall | null>, set: (c: IncomingCall) => void) => {
            if (!from) return;
            api.get('/api/crm/voice/lookup', { params: { phone: from } })
              .then((r) => {
                if (ref.current !== call) return;
                const name = r.data?.name || null;
                const ownerUserId = r.data?.owner_user_id || null;
                const ownerName = r.data?.owner_name || null;
                const lastContactedByName = r.data?.last_contacted_by_name || null;
                const lastContactedAt = r.data?.last_contacted_at || null;
                // Nothing useful resolved → keep the number-only popup as-is.
                if (!name && !ownerName && !lastContactedByName) return;
                set({
                  from,
                  name,
                  contactId: r.data?.contact_id || null,
                  ownerUserId,
                  ownerName,
                  lastContactedByName,
                  lastContactedAt,
                });
              })
              .catch(() => { /* number-only fallback */ });
          };
          if (curActive) {
            waitingRef.current = call;
            setWaiting({ from });
            resolveName(waitingRef, setWaiting);
            const clearW = () => {
              if (waitingRef.current === call) { waitingRef.current = null; setWaiting(null); }
            };
            call.on('cancel', clearW);     // caller hung up / Twilio ring timeout
            call.on('disconnect', clearW);
            call.on('reject', clearW);
            return;
          }
          incomingRef.current = call;
          setIncoming({ from });
          resolveName(incomingRef, setIncoming);
          const clear = () => {
            if (incomingRef.current === call) { incomingRef.current = null; setIncoming(null); }
          };
          call.on('cancel', clear);     // caller hung up before we answered
          call.on('disconnect', clear);
        });
        // Register so Twilio can ring this Device for inbound, and start the
        // presence heartbeat so inbound routing knows this agent is online.
        // register() is async — .catch so a transient failure never surfaces as
        // an unhandled rejection; the foreground recovery below retries it.
        try { device.register().catch(() => {}); } catch { /* register is best-effort */ }
        // The heartbeat carries the current live-call state on every TTL refresh
        // (read from the ref so this closure never needs to re-key the effect). 20s
        // (vs the 90s presence TTL) keeps a wide margin even when a backgrounded tab
        // throttles this interval toward ~60s — a too-thin margin is what made an
        // assigned recruiter look "offline" between beats and miss a lead's callback.
        const beat = () => { api.post('/api/crm/voice/presence', { call: callPresenceRef.current }).catch(() => {}); };
        beat();
        presenceTimer = setInterval(beat, 20000);
        // A backgrounded tab can have its timer throttled; beat immediately when the
        // agent returns to the foreground so presence is fresh before any inbound ring.
        // Phones fully SUSPEND the page (screen lock, app switch): tokenWillExpire
        // may never have fired and the signaling socket is dead, so on return also
        // mint a fresh token and re-register — otherwise the Call button silently
        // stops working until a full reload.
        visibilityHandler = () => {
          if (document.visibilityState !== 'visible') return;
          beat();
          refreshToken().then(() => {
            try {
              if (device && device.state !== Device.State.Registered && !callRef.current) {
                device.register().catch(() => {});
              }
            } catch { /* register stays best-effort */ }
          });
        };
        document.addEventListener('visibilitychange', visibilityHandler);
        deviceRef.current = device;
        // Saved speaker routing straight away — inbound rings play through
        // ringtoneDevices before any call exists. (Module-level helper reading
        // refs only: this effect must stay keyed on userId alone.)
        applySpeakerPref(device, outputDeviceIdRef.current);
        if (!cancelled) { setReady(true); setUnavailable(false); }
      } catch (e: any) {
        if (!cancelled) { setUnavailable(true); setReady(false); }
        console.error('[softphone] init failed:', e?.message || e);
      }
    })();

    return () => {
      cancelled = true;
      if (presenceTimer) clearInterval(presenceTimer);
      if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
      try { callRef.current?.disconnect(); } catch { /* noop */ }
      try { waitingRef.current?.reject(); } catch { /* noop */ }
      try { device?.destroy(); } catch { /* noop */ } // also unregisters on Twilio's side
      deviceRef.current = null;
      callRef.current = null;
      incomingRef.current = null;
      waitingRef.current = null;
    };
  }, [userId]);

  // Warn before a FULL-PAGE unload (tab close, refresh, typing a new URL,
  // closing the browser) ends an active call. The browser shows its own generic
  // dialog — the wording is not customizable for security reasons — but it forces
  // the user to confirm. In-app navigation out of the CRM is guarded separately
  // (CRMLayout) with a clear, worded modal.
  useEffect(() => {
    if (callStatus === 'idle') return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // required by some browsers to trigger the prompt
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [callStatus]);

  // Keep the screen awake while a call is live (phones): auto-lock suspends the
  // page and can kill the WebRTC media with it — a native dialer keeps the
  // screen on, so the softphone should too. Screen Wake Lock ships on iOS
  // 16.4+ and Android Chrome; elsewhere (and when the OS refuses, e.g. low
  // battery) this silently no-ops. The OS auto-releases the lock whenever the
  // page hides, so re-acquire on return while the call is still live.
  useEffect(() => {
    if (callStatus === 'idle') return;
    const wl = (navigator as any).wakeLock;
    if (!wl?.request) return;
    let sentinel: any = null;
    let stopped = false;
    const acquire = () => {
      try {
        wl.request('screen').then(
          (s: any) => {
            if (stopped) { try { s?.release?.(); } catch { /* noop */ } return; }
            sentinel = s;
          },
          () => { /* refused — cosmetic, never affects the call */ },
        );
      } catch { /* older engines throw synchronously — same no-op */ }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') acquire(); };
    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisible);
      try { sentinel?.release?.(); } catch { /* noop */ }
    };
    // Keyed on the idle/live flip only — status hops (connecting→ringing→open)
    // must not release + re-acquire the lock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callStatus === 'idle']);

  // Poll the shared parked-calls list (~10s) in its OWN effect — never folded
  // into the userId-keyed init effect above (re-keying that mid-call re-triggers
  // the ~90s call-drop bug). Stops permanently on a 503 (transfer not enabled).
  useEffect(() => {
    if (!userId) { setParkedCalls([]); return; }
    let stop = false;
    let unsupported = false;
    const poll = async () => {
      if (unsupported) return;
      try {
        const r = await api.get('/api/crm/voice/parked');
        if (!stop) setParkedCalls(r.data?.parked || []);
      } catch (e: any) {
        if (e?.response?.status === 503) { unsupported = true; if (!stop) setParkedCalls([]); }
      }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { stop = true; clearInterval(t); };
  }, [userId]);

  // Push the current live-call state to the server's voice presence. Additive
  // and fully defensive: a failed presence POST must never disrupt calling.
  // `setRef` keeps callPresenceRef in sync so the periodic heartbeat carries the
  // same state on its TTL refresh.
  const reportCallPresence = useCallback(
    (
      call: { on_call: true; contact_id: string | null; direction: 'outbound' | 'inbound'; started_at: string } | null,
      setRef = true,
    ) => {
      if (setRef) callPresenceRef.current = call;
      try {
        api.post('/api/crm/voice/presence', { call }).catch(() => {});
      } catch {
        /* never let presence reporting break a call */
      }
    },
    [],
  );

  const endLocal = useCallback(() => {
    // Stamp lastCall BEFORE clearing: only calls that have a contact AND
    // actually connected (startedAtRef set on 'accept') prompt for a rating.
    // Inbound accepted calls set active WITHOUT a contactId — deliberately
    // unprompted in Phase G (no client-side contact lookup). A park/transfer
    // handoff (transitionRef set) ends the agent's leg but the call lives on
    // elsewhere, so it must NOT prompt a rating either.
    const handoff = transitionRef.current;
    const ended = activeRef.current;
    const connectedAt = startedAtRef.current;
    if (!handoff && ended?.contactId && connectedAt != null) {
      setLastCall({
        contactId: ended.contactId,
        name: ended.name,
        callSid: callRef.current?.parameters?.CallSid || null,
        durationMs: Date.now() - connectedAt,
      });
    }
    transitionRef.current = null;
    setTransition(null);
    directionRef.current = null;
    activeRef.current = null;
    startedAtRef.current = null;
    callRef.current = null;
    // Release the mic the SDK keeps open once setInputDevice has been used —
    // the tab's recording indicator must not stay lit after hangup. The saved
    // preference re-applies at the next call's start (applyInputPref).
    try { (deviceRef.current as any)?.audio?.unsetInputDevice()?.catch?.(() => {}); } catch { /* noop */ }
    setCallStatus('idle');
    setActive(null);
    setStartedAt(null);
    setMuted(false);
    setReconnecting(false);
    // Report the call ended so the Calls dashboard / inbound routing clear it.
    reportCallPresence(null);
  }, [reportCallPresence]);

  // Keep endLocalRef pointed at the latest endLocal so the init effect's 'incoming'
  // handler can clean up a stale/closed call ref without being re-keyed on it.
  useEffect(() => { endLocalRef.current = endLocal; }, [endLocal]);

  const clearLastCall = useCallback(() => setLastCall(null), []);

  // Attach the disconnect/cancel/reject/error teardown to a call, GUARDED by call
  // identity: each handler runs endLocal only while this call is still the active one
  // (callRef.current). A call we've swapped away from (answering a call-waiting call)
  // therefore can't null out the newly-adopted call when its own 'disconnect' finally
  // fires — no fragile removeListener-by-reference dance needed, and robust even if
  // endLocal's identity ever changes.
  const wireCallEndHandlers = useCallback((call: TCall) => {
    const endIfCurrent = () => { if (callRef.current === call) endLocal(); };
    call.on('disconnect', endIfCurrent);
    call.on('cancel', endIfCurrent);
    call.on('reject', endIfCurrent);
    // Media drop mid-call (cellular blip / wifi→LTE handoff): the SDK retries
    // within the signaling-reconnect window — surface it so the recruiter knows
    // to hold on instead of assuming the call died and hanging up.
    call.on('reconnecting', () => { if (callRef.current === call) setReconnecting(true); });
    call.on('reconnected', () => { if (callRef.current === call) setReconnecting(false); });
    call.on('error', (e: any) => {
      if (callRef.current !== call) return; // a swapped-away call must not surface errors
      setError(e?.message || 'Call error');
      endLocal();
    });
  }, [endLocal]);

  const startCall = useCallback(async ({ contactId, to, name }: ActiveContact) => {
    const device = deviceRef.current;
    if (!device || callRef.current || !to) return;
    setError(null);
    // Mic preflight, tied to the Call tap's user gesture. device.connect() asks
    // for the mic anyway, but doing it explicitly (a) makes the OS permission
    // prompt appear for THIS tap on phones (iOS home-screen apps re-ask every
    // launch) and (b) turns a denial into actionable copy instead of the SDK's
    // generic AcquisitionFailedError. The probe stream is released immediately.
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Calling is not supported in this browser — the microphone API is unavailable.');
      return;
    }
    try {
      const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
      probe.getTracks().forEach((t) => t.stop());
    } catch (e) {
      setError(micErrorMessage(e));
      return;
    }
    setLastCall(null); // the rating prompt must never block the next call
    directionRef.current = 'outbound';
    activeRef.current = { contactId, to, name };
    setActive({ contactId, to, name });
    setCallStatus('connecting');
    // Saved mic/speaker for THIS call. Both fail soft (applyInputPref never
    // rejects) — an unplugged saved device must never block a call.
    await applyInputPref(device, inputDeviceIdRef.current);
    applySpeakerPref(device, outputDeviceIdRef.current);
    try {
      const call = await device.connect({ params: { To: to, contactId: contactId || '' } });
      callRef.current = call;
      setMuted(false);
      call.on('ringing', () => setCallStatus('ringing'));
      call.on('accept', () => {
        const now = Date.now();
        startedAtRef.current = now;
        setCallStatus('open');
        setStartedAt(now);
        // Live-call presence: the outbound call connected.
        reportCallPresence({
          on_call: true,
          contact_id: contactId || null,
          direction: 'outbound',
          started_at: new Date(now).toISOString(),
        });
      });
      wireCallEndHandlers(call);
    } catch (e: any) {
      setError(e?.message || 'Could not start the call (microphone permission may be required).');
      endLocal();
    }
  }, [endLocal, reportCallPresence, wireCallEndHandlers]);

  const hangup = useCallback(() => {
    try { callRef.current?.disconnect(); } catch { /* noop */ }
  }, []);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !muted;
    try { call.mute(next); setMuted(next); } catch { /* noop */ }
  }, [muted]);

  // DTMF: forward dial-pad presses to the live call. Only digits/*/# are valid;
  // a no-op when no call is active (the dial pad builds the number string then).
  const sendDigits = useCallback((digits: string) => {
    const call = callRef.current;
    const clean = String(digits || '').replace(/[^0-9*#]/g, '');
    if (!call || !clean) return;
    try { call.sendDigits(clean); } catch { /* noop */ }
  }, []);

  // Wire + accept an inbound Call as the active call — shared by acceptIncoming and
  // acceptWaiting. Inbound active state carries no contactId, so it never prompts for
  // a rating (Phase G). Assumes any prior call has already been torn down.
  const adoptInboundCall = useCallback((call: TCall, name?: string | null) => {
    callRef.current = call;
    setError(null);
    setLastCall(null); // a lingering rating prompt must never sit over a live call
    setMuted(false);
    const fromNum = (call.parameters && call.parameters.From) || undefined;
    const inboundActive: ActiveContact = {
      to: fromNum || 'Incoming call',
      name: name || fromNum || undefined,
    };
    directionRef.current = 'inbound';
    activeRef.current = inboundActive; // no contactId → never prompts for a rating
    setActive(inboundActive);
    call.on('accept', () => {
      const now = Date.now();
      startedAtRef.current = now;
      setCallStatus('open');
      setStartedAt(now);
      // Live-call presence: the inbound call was answered. No contact id is
      // resolved client-side for inbound, so report it as null.
      reportCallPresence({
        on_call: true,
        contact_id: null,
        direction: 'inbound',
        started_at: new Date(now).toISOString(),
      });
    });
    wireCallEndHandlers(call);
    setCallStatus('connecting');
    // Saved mic/speaker first (fail-soft; applyInputPref never rejects), then
    // accept — the SDK acquires its input stream during accept().
    applySpeakerPref(deviceRef.current, outputDeviceIdRef.current);
    applyInputPref(deviceRef.current, inputDeviceIdRef.current).then(() => {
      try { call.accept(); } catch (e: any) { setError(e?.message || 'Could not answer the call'); endLocal(); }
    });
  }, [endLocal, reportCallPresence, wireCallEndHandlers]);

  const acceptIncoming = useCallback(() => {
    const call = incomingRef.current;
    if (!call) return;
    incomingRef.current = null;
    setIncoming(null);
    adoptInboundCall(call, incoming?.name);
  }, [adoptInboundCall, incoming]);

  const rejectIncoming = useCallback(() => {
    const call = incomingRef.current;
    if (call) { try { call.reject(); } catch { /* noop */ } }
    incomingRef.current = null;
    setIncoming(null);
  }, []);

  // Answer the call-waiting call: adopt it as the active call FIRST (which repoints
  // callRef to it), THEN hang up the previous call. The previous call's teardown is
  // identity-guarded (wireCallEndHandlers), so its imminent 'disconnect' sees it's no
  // longer the active call and no-ops — it can't null out the call we just adopted.
  // Like swapping to line 2.
  const acceptWaiting = useCallback(() => {
    const wc = waitingRef.current;
    if (!wc) return;
    waitingRef.current = null;
    setWaiting(null);
    const prev = callRef.current;
    adoptInboundCall(wc, waiting?.name);
    if (prev && prev !== wc) { try { prev.disconnect(); } catch { /* noop */ } }
  }, [adoptInboundCall, waiting]);

  const declineWaiting = useCallback(() => {
    const call = waitingRef.current;
    if (call) { try { call.reject(); } catch { /* noop */ } }
    waitingRef.current = null;
    setWaiting(null);
  }, []);

  // Park the live call: the server moves the CUSTOMER leg into a conference on
  // hold music; our bridge leg then drops (handled as a 'parking' transition so
  // no rating prompt fires). The call reappears in everyone's parked panel.
  const park = useCallback(async () => {
    const call = callRef.current;
    const agentLegSid = call?.parameters?.CallSid;
    if (!call || !agentLegSid) { setError('This call can’t be parked.'); return; }
    transitionRef.current = 'parking';
    setTransition('parking');
    try {
      await api.post('/api/crm/voice/park', { agentLegSid, direction: directionRef.current });
      try { call.disconnect(); } catch { /* the server teardown drops it anyway */ }
    } catch (e: any) {
      transitionRef.current = null;
      setTransition(null);
      setError(e?.response?.data?.error || 'Could not park the call.');
    }
  }, []);

  // Blind transfer the live call to another online agent (Client-only, test-safe).
  const transferBlind = useCallback(async (targetUserId: string) => {
    const call = callRef.current;
    const agentLegSid = call?.parameters?.CallSid;
    if (!call || !agentLegSid || !targetUserId) { setError('This call can’t be transferred.'); return; }
    transitionRef.current = 'transferring';
    setTransition('transferring');
    try {
      await api.post('/api/crm/voice/transfer/blind', { agentLegSid, direction: directionRef.current, targetUserId });
      try { call.disconnect(); } catch { /* the server teardown drops it anyway */ }
    } catch (e: any) {
      transitionRef.current = null;
      setTransition(null);
      setError(e?.response?.data?.error || 'Could not transfer the call.');
    }
  }, []);

  // Pick up a parked call: join its conference from the browser, then clear the
  // parked state so it leaves everyone's panel.
  const pickUp = useCallback(async (confName: string, customerLegSid: string) => {
    const device = deviceRef.current;
    if (!device || callRef.current || !confName) return;
    setError(null);
    setLastCall(null);
    directionRef.current = 'inbound';
    const picked: ActiveContact = { to: 'Parked call', name: 'Parked call' };
    activeRef.current = picked;
    setActive(picked);
    setCallStatus('connecting');
    // Same saved-device application as startCall — fail-soft, never blocking.
    await applyInputPref(device, inputDeviceIdRef.current);
    applySpeakerPref(device, outputDeviceIdRef.current);
    try {
      const call = await device.connect({ params: { Conf: confName } });
      callRef.current = call;
      setMuted(false);
      call.on('ringing', () => setCallStatus('ringing'));
      call.on('accept', () => {
        const now = Date.now();
        startedAtRef.current = now;
        setCallStatus('open');
        setStartedAt(now);
        // A picked-up parked call is a live (inbound-origin) call too.
        reportCallPresence({
          on_call: true,
          contact_id: null,
          direction: 'inbound',
          started_at: new Date(now).toISOString(),
        });
      });
      wireCallEndHandlers(call);
      api.post('/api/crm/voice/unpark', { customerLegSid }).catch(() => { /* best-effort */ });
    } catch (e: any) {
      setError(e?.message || 'Could not pick up the call.');
      endLocal();
    }
  }, [endLocal, reportCallPresence, wireCallEndHandlers]);

  const clearError = useCallback(() => setError(null), []);

  const inCall = callStatus !== 'idle';

  return (
    <SoftphoneContext.Provider
      value={{ ready, unavailable, callStatus, inCall, muted, reconnecting, error, active, incoming, waiting, startedAt, lastCall, transition, parkedCalls, startCall, hangup, toggleMute, sendDigits, acceptIncoming, rejectIncoming, acceptWaiting, declineWaiting, park, transferBlind, pickUp, clearError, clearLastCall, inputDevices, outputDevices, inputDeviceId, outputDeviceId, defaultInputLabel, defaultOutputLabel, outputSelectionSupported, setAudioInputDevice, setAudioOutputDevice, refreshAudioDevices }}
    >
      {children}
    </SoftphoneContext.Provider>
  );
}

export default SoftphoneProvider;
